// Zeiterfassung (/rechnung/zeiten, server/src/modules/rechnungstool/zeiten.ts): Antworten des Servers, Adressen und
// die Positionen einer Rechnung aus Zeiten (ohne Oberfläche). Gespeichert sind je Eintrag nur Tag und Dauer in Minuten.
import { isHourly } from '../empfehlung';
import type { Leistung } from '../LeistungDaten';
import { dePrice } from '../formular';
import { stundenPositionen, taetigkeitKey, type KiPosition, type PositionsArt, type StundenZeile } from '../stundenPositionen';
import type { Rate } from '../types';

export interface ZeitKunde {
  id: string;
  nummer: number | null;
  name: string;
}

// wer gearbeitet hat (nur für den, der alle Zeiten sieht); id null: früheres Teammitglied
export interface ZeitPerson {
  id: string | null;
  name: string;
}

export interface Zeit {
  id: string;
  datum: string;
  minuten: number;
  kunde: ZeitKunde | null;
  taetigkeit: string | null;
  objekt: string | null;
  // false: „nicht berechnen“ (Kulanz, intern)
  berechnen: boolean;
  // berechnet mit dieser Rechnung (nur, solange sie gilt; nach einem Storno wieder null)
  rechnung: { id: string; number: string } | null;
  person?: ZeitPerson;
  // vom eigenen Login
  eigene: boolean;
}

// laufende Uhr; laeuftSeit nur bei der eigenen
export interface Uhr {
  id: string;
  laeuftSeit?: string;
  kunde: ZeitKunde | null;
  taetigkeit: string | null;
  objekt: string | null;
}

export type ZeitStatus = 'alle' | 'offen' | 'berechnet';

export interface ZeitListe {
  // alle: Inhaber oder Bereich „Zeiten des Teams“ (sonst nur die eigenen)
  sicht: { alle: boolean };
  monat: string;
  // Uhr des Servers beim Antworten (Abstand zur Uhr des Geräts)
  jetzt: string;
  uhr: Uhr | null;
  // laufende Uhren der anderen (nur wer alle sieht), ohne Start und Dauer
  laufend: { id: string; person: ZeitPerson }[];
  zeiten: Zeit[];
  // mehr als 2.000 Treffer
  mehr: boolean;
  // Minuten aller Treffer, davon offen
  summe: number;
  offen: number;
  personen?: { id: string; name: string }[];
  vorschlaege: { taetigkeiten: string[]; objekte: string[] };
}

export interface ZeitFilter {
  // „YYYY-MM“ oder „alle“
  monat: string;
  status: ZeitStatus;
  kunde: string | null;
  person: string | null;
}

// Eintrag anlegen oder ändern (leere Texte: null)
export interface ZeitBody {
  datum: string;
  minuten: number;
  kundeId: string | null;
  taetigkeit: string | null;
  objekt: string | null;
  berechnen: boolean;
  personId?: string;
}

const API = '/api/rechnungstool';
const query = (f: Partial<ZeitFilter>) => {
  const p = new URLSearchParams();
  if (f.monat) p.set('monat', f.monat);
  if (f.status && f.status !== 'alle') p.set('status', f.status);
  if (f.kunde) p.set('kunde', f.kunde);
  if (f.person) p.set('person', f.person);
  const s = p.toString();
  return s ? `?${s}` : '';
};
export const zeitenUrl = (firmId: string, f: Partial<ZeitFilter> = {}) => `${API}/${firmId}/zeiten${query(f)}`;
export const zeitenCsvUrl = (firmId: string, f: Partial<ZeitFilter>) => `${API}/${firmId}/zeiten/export${query(f)}`;
export const zeitenEntwurfUrl = (firmId: string) => `${API}/${firmId}/zeiten/entwurf`;
export const zeitUrl = (id: string) => `${API}/zeit/${id}`;
export const uhrStartUrl = (firmId: string) => `${API}/${firmId}/uhr`;
export const uhrUrl = (id: string) => `${API}/uhr/${id}`;

// noch zu berechnen: nicht „nicht berechnen“ und ohne gültige Rechnung
export const istOffen = (z: Zeit) => z.berechnen && !z.rechnung;
export const summeMinuten = (list: Pick<Zeit, 'minuten'>[]) => list.reduce((s, z) => s + z.minuten, 0);
// „1 Zeit“, „12 Zeiten“
export const zeitenText = (n: number) => (n === 1 ? '1 Zeit' : `${n} Zeiten`);
export const eintraegeText = (n: number) => (n === 1 ? '1 Eintrag' : `${n} Einträge`);

// Positionen der Rechnung aus den abgehakten Zeiten (Dialog „Rechnung aus Zeiten“): je Zeit eine Zeile mit den Stunden,
// bei mehreren Einsatzorten „<Objekt>: <Tätigkeit>“. Eine Tätigkeit mit einer Leistung gleichen Namens in Std. bekommt
// deren Preis und Steuersatz (nur bei Kunden in Euro, Leistungen haben Euro-Preise), sonst den Stundensatz; „eine
// Position“ immer den Stundensatz. Namen nur „je Eintrag“ und nur auf Wunsch.
export function zeitenPositionen(
  zeilen: Zeit[],
  o: { art: PositionsArt; preis: string; namen: boolean; mehrereObjekte: boolean; leistungen: Leistung[]; euro: boolean },
): KiPosition[] {
  // Text der Tätigkeit auf der Rechnung → Tätigkeit ohne Objekt (für den Preis aus der Leistung)
  const ohneObjekt = new Map<string, string | null>();
  const stunden: StundenZeile[] = zeilen.map((z) => {
    const t = o.mehrereObjekte && z.objekt ? `${z.objekt}: ${z.taetigkeit ?? 'Arbeitsstunden'}` : z.taetigkeit;
    if (t) ohneObjekt.set(t, z.taetigkeit);
    return { datum: z.datum, name: z.person?.name ?? null, taetigkeit: t, stunden: z.minuten / 60 };
  });
  const preisFuer = o.euro
    ? (t: string | null) => {
        const l = leistungFuer(o.leistungen, t === null ? null : (ohneObjekt.get(t) ?? t));
        return l ? { price: dePrice(l.price!), rate: l.rate } : null;
      }
    : undefined;
  return stundenPositionen(stunden, { art: o.art, preis: o.preis, namen: o.namen && o.art === 'einzeln', quelle: 'zeiten', preisFuer });
}

// Steuersatz einer Position im Entwurf: der Satz ihrer Leistung (zeitenPositionen), sonst der Satz der Stunden-Leistung
// (standard), sonst 19 %; nur Sätze, die der Absender nehmen kann (ratesFor)
export function satzFuer(p: KiPosition, standard: Rate | undefined, saetze: Rate[]): Rate {
  if (p.rate !== undefined && saetze.includes(p.rate)) return p.rate;
  return standard !== undefined && saetze.includes(standard) ? standard : 19;
}

// Leistung in Std. mit dem Namen der Tätigkeit und festem Preis
export function leistungFuer(leistungen: Leistung[], taetigkeit: string | null): Leistung | null {
  const k = taetigkeitKey(taetigkeit);
  if (!k) return null;
  return leistungen.find((l) => l.price != null && isHourly(l.unit) && taetigkeitKey(l.name) === k) ?? null;
}
